import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

import { useUserStore } from '../stores/user.store';
import JwtService from '../services/jwt.service'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: '',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    meta: { requiresAuth: true },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'my-licenses',
        path: 'my-licenses',
        component: () => import('../pages/my-licenses/MyLicensesPage.vue'),
        redirect: { name: 'my-licenses-requests' }, // Default to the first tab
        meta: { requiresAuth: true, title: 'My Licenses' },
        children: [
          {
            name: 'my-licenses-requests',
            path: 'requests',
            component: () => import('../pages/my-licenses/LicenseRequestsTabPage.vue'), // You'll need to create this
            meta: { requiresAuth: true, title: 'My License Requests' },
          },
          {
            name: 'my-licenses-purchased',
            path: 'purchased',
            component: () => import('../pages/my-licenses/PurchasedLicensesTabPage.vue'), // You'll need to create this
            meta: { requiresAuth: true, title: 'My Issued Licenses' },
          },
        ],
      },
      {
        name: 'license-requests-user',
        path: 'license-requests',
        component: () => import('../pages/license-requests-user/LicenseRequestsUser.vue'),
        meta: { requiresAuth: true, title: 'License Requests' },
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'updates',
        path: 'updates',
        component: () => import('../pages/updates/Updates.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
      },
      {
        name: 'payments',
        path: 'payments',
        component: () => import('../pages/payments/PaymentsPage.vue'),
      },
      {
        name: 'billing',
        path: 'billing',
        component: () => import('../pages/billing/BillingPage.vue'),
      },
      {
        name: 'pricing-plans',
        path: 'pricing-plans',
        component: () => import('../pages/pricing-plans/PricingPlans.vue'),
      },
      {
        name: 'custom-license',
        path: 'pricing-plans/custom-license',
        component: () => import('../pages/pricing-plans/CustomLicense.vue'),
      },
      {
        name: 'plan-confirmation',
        path: 'pricing-plans/confirmation/:planId',
        component: () => import('../pages/pricing-plans/PlanConfirmation.vue'),
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
      {
        name: 'threat-intelligence',
        path: 'threat-intelligence',
        component: () => import('../pages/threat-intelligence/ThreatIntelligence.vue'),
        meta: { requiresAuth: true, title: 'Threat Intelligence' },
      },
      {
        name: 'admin-panel',
        path: 'admin',
        component: RouteViewComponent,
        meta: { requiresAuth: true, adminOnly: true },
        children: [
          {
            name: 'admin-panel-dashboard',
            path: 'dashboard',
            component: () => import('../pages/admin/panel/AdminPanel.vue'),
          },
          {
            name: 'license-requests',
            path: 'license-requests',
            component: () => import('../pages/license-requests/LicenseRequests.vue'),
          },
          {
            name: 'template-plan',
            path: 'plans',
            component: RouteViewComponent,
            children: [
              {
                name: 'template-plans-list',
                path: '',
                component: () => import('../pages/admin/template-plan/TemplatePlanList.vue'),
              },
              {
                name: 'template-plans-view',
                path: ':id',
                component: () => import('../pages/admin/template-plan/TemplatePlanView.vue'),
              },
              {
                name: 'template-plans-edit',
                path: ':id/edit',
                component: () => import('../pages/admin/template-plan/TemplatePlanEdit.vue'),
              },
              {
                name: 'template-plans-create',
                path: 'create',
                component: () => import('../pages/admin/template-plan/TemplatePlanCreate.vue'),
              },
            ],
          },
          {
            name: 'users-admin',
            path: 'users',
            component: () => import('../pages/users/UsersPage.vue'),
          },
          {
            name: 'admin-resolution-center',
            path: 'resolution-center',
            component: () => import('@/pages/admin/panel/components/AdminResolutionCenter.vue')
          },
          {
            name: 'admin-ticket-details',
            path: 'resolution-center/:uuid',
            component: () => import('@/pages/admin/panel/components/AdminTicketDetails.vue'),
            props: true
          },
          {
            name: 'admin-purchased-licenses',
            path: 'purchased-licenses',
            component: () => import('@/pages/admin/panel/AdminPurchasedLicenses.vue'),
          },
          {
            name: 'admin-manage-features',
            path: 'manage-features',
            component: () => import('@/pages/admin/manage-features/ManageFeatures.vue'),
          },
          {
            name: 'admin-manage-services',
            path: 'manage-services',
            component: () => import('@/pages/admin/manage-services/ManageServices.vue'),
          },
          {
            name: 'admin-log-monitor',
            path: 'log-monitor',
            component: () => import('@/pages/admin/log-monitor/LogMonitor.vue'),
          },
          {
            name: 'admin-mfa-authenticator',
            path: 'mfa-authenticator',
            component: () => import('@/pages/admin/mfa-authenticator/MfaAuthenticator.vue'),
          },
          {
            name: 'admin-updates',
            path: 'admin-updates',
            component: () => import('@/pages/admin/updates/Updates.vue'),
          }
        ],
      },
      {
        name: 'user-resolution-center',
        path: 'my-tickets',
        component: () => import('@/pages/user/UserResolutionCenterPage.vue'),
        meta: { requiresAuth: true }
      },
      {
        name: 'user-ticket-details',
        path: 'my-tickets/:uuid',
        component: () => import('@/pages/user/UserTicketDetailsPage.vue'),
        meta: { requiresAuth: true },
        props: true
      },
      {
        name: 'user-create-ticket',
        path: 'create-new-ticket',
        component: () => import('@/pages/user/UserCreateTicketPage.vue'),
        meta: { requiresAuth: true }
      },
      {
        name: 'purchased-licenses',
        path: 'purchased-licenses',
        component: () => import('../pages/purchased-licenses/PurchasedLicensesPage.vue'),
        meta: { requiresAuth: true, title: 'Issued Licenses' },
      },
      // {
      //   name: 'profile', // User profile page
      //   path: 'profile',
      //   component: () => import('../pages/user/UserProfilePage.vue'), // Adjusted path assuming it exists
      //   meta: { requiresAuth: true }
      // }
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/RecoverInfo.vue'),
      },
      {
        name: 'reset-password-force',
        path: 'reset-password-force',
        component: () => import('../pages/auth/ResetPasswordForce.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    path: '/license-requests',
    component: () => import('../pages/license-requests/LicenseRequests.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
  {
    path: '/license-requests/edit/:id',
    component: () => import('../pages/license-requests/LicenseRequestDetails.vue'),
    meta: {
      requiresAuth: true,
      requiresAdmin: true
    }
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})


//? navigation guard to protect routes that require authentication:
router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const isAuthenticated = JwtService.hasToken();

  // If user is authenticated and tries to access auth pages (login, signup, etc.), redirect to dashboard
  if (isAuthenticated && to.matched.some(record => record.path.startsWith('/auth'))) {
    // Exception for forced reset password page
    if (to.name !== 'reset-password-force') {
      return next({ name: 'dashboard' });
    }
  }

  if (requiresAuth && !isAuthenticated) {
    return next({ name: 'login' });
  }

  // If token exists but user data is not loaded yet
  if (isAuthenticated && !userStore.user && to.name !== 'login') {
    try {
      await userStore.fetchCurrentUser();
    } catch (error) {
      // If token is invalid or expired
      return next({ name: 'login' });
    }
  }

  // Check for forced password reset
  if (isAuthenticated && userStore.user?.reset_required) {
    if (to.name !== 'reset-password-force') {
      return next({ name: 'reset-password-force' });
    }
    // Allow access to the reset page
    return next();
  }

  // Admin routes protection
  if (to.matched.some(record => record.meta.adminOnly)) {
    // If user is not authenticated, redirect to login
    if (!userStore.isAuthenticated) {
      next({ name: 'login', query: { redirect: to.fullPath } });
      return;
    }

    // If user is authenticated but not admin, redirect to dashboard
    if (!userStore.isAdmin) {
      next({ name: 'dashboard' });
      return;
    }

    // If user is authenticated and is admin, allow access
    next();
  } else {
    next();
  }
});

export default router
